import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../v32/components/ui/card';
import { UI_FLAGS as FLAGS } from '../../lib/flags';

export default function AdminSettings() {
  return (
    <div className="space-y-6">
      {/* Feature Flags */}
      <section>
        <h2 className="text-lg font-semibold mb-4 text-neutral-800">Feature Flags</h2>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Development Features</CardTitle>
            <CardDescription>
              Features that are enabled based on environment configuration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <FeatureFlagRow 
                name="Voice Inspector" 
                enabled={FLAGS.showVoiceInspector}
                description="Show voice analysis and debugging tools"
              />
              <FeatureFlagRow 
                name="Demo Ribbons" 
                enabled={FLAGS.showDemoRibbons}
                description="Display demo mode indicators"
              />
              <FeatureFlagRow 
                name="PMF Metrics Tile" 
                enabled={FLAGS.enablePMFTile}
                description="Enable Product-Market Fit metrics dashboard tile"
              />
              <FeatureFlagRow 
                name="Verbose Logs" 
                enabled={FLAGS.enableVerboseLogs}
                description="Enable detailed logging for debugging"
              />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* System Information */}
      <section>
        <h2 className="text-lg font-semibold mb-4 text-neutral-800">System Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Environment</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Mode:</span>
                  <span className="font-mono text-neutral-800">
                    {import.meta.env.DEV ? 'development' : 'production'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Build:</span>
                  <span className="font-mono text-neutral-800">
                    {typeof window !== 'undefined' && (import.meta as any).env?.MODE || 'unknown'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Page Load:</span>
                  <span className="font-mono text-neutral-800">
                    {performance.timing ? 
                      `${performance.timing.loadEventEnd - performance.timing.navigationStart}ms` : 
                      'N/A'
                    }
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Memory:</span>
                  <span className="font-mono text-neutral-800">
                    {(performance as any).memory ? 
                      `${Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024)}MB` : 
                      'N/A'
                    }
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Actions */}
      <section>
        <h2 className="text-lg font-semibold mb-4 text-neutral-800">Administrative Actions</h2>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="p-4 bg-neutral-50 rounded-lg">
                <h3 className="font-medium text-sm text-neutral-800 mb-2">Cache Management</h3>
                <p className="text-xs text-neutral-600 mb-3">
                  Clear application cache and temporary data
                </p>
                <button 
                  className="px-3 py-1.5 bg-neutral-200 hover:bg-neutral-300 text-neutral-700 text-xs rounded transition-colors"
                  onClick={() => {
                    if ('caches' in window) {
                      caches.keys().then(names => {
                        names.forEach(name => caches.delete(name));
                      });
                    }
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.reload();
                  }}
                >
                  Clear Cache & Reload
                </button>
              </div>

              <div className="p-4 bg-neutral-50 rounded-lg">
                <h3 className="font-medium text-sm text-neutral-800 mb-2">Debug Information</h3>
                <p className="text-xs text-neutral-600 mb-3">
                  Download system information for debugging
                </p>
                <button 
                  className="px-3 py-1.5 bg-neutral-200 hover:bg-neutral-300 text-neutral-700 text-xs rounded transition-colors"
                  onClick={() => {
                    const debugInfo = {
                      timestamp: new Date().toISOString(),
                      userAgent: navigator.userAgent,
                      url: window.location.href,
                      flags: FLAGS,
                      env: import.meta.env.DEV ? 'development' : 'production',
                      memory: (performance as any).memory,
                      timing: performance.timing
                    };
                    const blob = new Blob([JSON.stringify(debugInfo, null, 2)], { 
                      type: 'application/json' 
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `debug-info-${new Date().getTime()}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Download Debug Info
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

interface FeatureFlagRowProps {
  name: string;
  enabled: boolean;
  description: string;
}

function FeatureFlagRow({ name, enabled, description }: FeatureFlagRowProps) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-50">
      <div>
        <div className="text-sm font-medium text-neutral-800">{name}</div>
        <div className="text-xs text-neutral-600">{description}</div>
      </div>
      <div className="flex items-center space-x-2">
        <span className={`text-xs px-2 py-1 rounded font-medium ${
          enabled 
            ? 'bg-success-100 text-success-700'
            : 'bg-neutral-200 text-neutral-600'
        }`}>
          {enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>
    </div>
  );
}
